import { useProjectMembersStore } from '@/stores/projectMembersStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { ProjectRoleValue, WorkspaceRoleValue, type ProjectRole, type WorkspaceRole } from '@/api/types'

export type ProjectAction =
  | 'view'
  | 'edit'
  | 'create_commit'
  | 'import_sql'
  | 'rename'
  | 'manage_members'
  | 'transfer_owner'
  | 'delete'

function minProjectRole(action: ProjectAction): ProjectRole {
  switch (action) {
    case 'view':
      return ProjectRoleValue.VIEWER
    case 'edit':
    case 'create_commit':
    case 'import_sql':
      return ProjectRoleValue.EDITOR
    case 'rename':
    case 'manage_members':
      return ProjectRoleValue.ADMIN
    case 'transfer_owner':
      return ProjectRoleValue.OWNER
    case 'delete':
      return ProjectRoleValue.OWNER
  }
}

export function projectCan(
  action: ProjectAction,
  role: ProjectRole | null,
  wsRole: WorkspaceRole | null,
): boolean {
  if (action === 'transfer_owner') {
    if (wsRole === WorkspaceRoleValue.OWNER) return true
    if (role === null) return false
    return role <= ProjectRoleValue.ADMIN
  }

  const isWsAdminOrOwner = wsRole !== null && wsRole <= WorkspaceRoleValue.ADMIN
  if (isWsAdminOrOwner) return true
  if (role === null) return false
  return role <= minProjectRole(action)
}

export function useProjectCan(action: ProjectAction): boolean {
  const role = useProjectMembersStore((s) => s.myProjectRole)
  const wsRole = useWorkspaceStore((s) => s.myRole)

  if (action === 'transfer_owner') {
    if (wsRole === WorkspaceRoleValue.OWNER) return true
    if (role === null) return false
    return role <= ProjectRoleValue.ADMIN
  }

  // WS owner/admin are implicitly elevated — they can perform all project actions (D5/D7).
  const isWsAdminOrOwner = wsRole !== null && wsRole <= WorkspaceRoleValue.ADMIN
  if (isWsAdminOrOwner) return true

  if (role === null) return false
  return role <= minProjectRole(action)
}
